import React, { useEffect, useState } from 'react';
import { initMetaPixel } from './utils/metaPixel';
import { AnnouncementBar } from './components/AnnouncementBar';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { TrustBand } from './components/TrustBand';
import { BenefitsSection } from './components/BenefitsSection';
import { BeforeAfterSection } from './components/BeforeAfterSection';
import { ProductCollection } from './components/ProductCollection';
import { CompleteCollection } from './components/CompleteCollection';
import { PremiumGallery } from './components/PremiumGallery';
import { ContentPreview } from './components/ContentPreview';
import { Testimonials } from './components/Testimonials';
import { Guarantee } from './components/Guarantee';
import { CartSummary } from './components/CartSummary';
import { FAQ } from './components/FAQ';
import { FinalCTA } from './components/FinalCTA';
import { Footer } from './components/Footer';
import { PurchaseToast } from './components/PurchaseToast';
import { ExitIntentUpsellPopup } from './components/ExitIntentUpsellPopup';
import { ExtraComplementUpsellPopup } from './components/ExtraComplementUpsellPopup';
import { SecondKitUpsellPopup } from './components/SecondKitUpsellPopup';
import { BundleUpsellPopup } from './components/BundleUpsellPopup';
import { UPSELLS } from './data/upsells';
import { KITS } from './data/kits';
import { ScrollProgressBar } from './components/ScrollProgressBar';
import { PremiumLoader } from './components/PremiumLoader';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { PaymentMethods } from './components/PaymentMethods';
import { goToShopifyCart, goToShopifyCartFullPack, goToShopifyCartTwoComplementsPromo, goToShopifyCartTwoProductsPromo, goToShopifyCartTwoKitsPromo, goToShopifyCartBundlePromo } from './data/shopify';
// Upsell funnel steps:
// 'none'      = no popup open
// 'secondKit' = add a 2nd main kit, both for $22.990 (when exactly 1 main kit, 0 complements)
// 'first'     = kit + 1 complement for $22.980 (other 0-complement cases)
// 'extra'     = add a 2nd complement, both for $19.990 (when 1 complement chosen)
// 'bundle'    = all 6 complements at 30% OFF (escalation when an offer is declined)
type UpsellStep = 'none' | 'secondKit' | 'first' | 'extra' | 'bundle';
export function App() {
  const [selectedKitIds, setSelectedKitIds] = useState<string[]>([]);
  const [selectedUpsellIds, setSelectedUpsellIds] = useState<string[]>([]);
  const [upsellStep, setUpsellStep] = useState<UpsellStep>('none');
  // Once the upsell funnel has been shown and resolved, checkout goes
  // straight to Shopify on subsequent clicks.
  const [upsellResolved, setUpsellResolved] = useState(false);
  // Meta Pixel: init + PageView once on load.
  useEffect(() => {
    initMetaPixel();
  }, []);
  const handleToggleKit = (id: string) => {
    setSelectedKitIds(prev => prev.includes(id) ? prev.filter(kitId => kitId !== id) : [...prev, id]);
  };
  const handleRemoveKit = (id: string) => {
    setSelectedKitIds(prev => prev.filter(kitId => kitId !== id));
  };
  const handleToggleUpsell = (id: string) => {
    setSelectedUpsellIds(prev => prev.includes(id) ? prev.filter(upsellId => upsellId !== id) : [...prev, id]);
  };
  const handleRemoveUpsell = (id: string) => {
    setSelectedUpsellIds(prev => prev.filter(upsellId => upsellId !== id));
  };
  /**
   * Checkout flow:
   * - No kit selected → nudge user to pick one
   * - Funnel NOT yet resolved → open the first upsell offer
   * - Funnel already resolved → go straight to Shopify
   */
  const handleCheckoutAttempt = () => {
    // Only block when the cart is completely empty. A customer who picked
    // just a complementary kit can continue without a main kit.
    const hasAnyItem = selectedKitIds.length > 0 || selectedUpsellIds.length > 0;
    if (!hasAnyItem) {
      alert('Por favor, elegí al menos un kit para continuar.');
      document.getElementById('kits')?.scrollIntoView({
        behavior: 'smooth'
      });
      return;
    }
    if (!upsellResolved) {
      // Branch the funnel by what's already in the cart.
      if (selectedKitIds.length === 0 && selectedUpsellIds.length === 1) {
        // ONLY one complementary kit (no main kit) → offer a 2nd complement,
        // both for $19.990.
        setUpsellStep('extra');
      } else if (selectedUpsellIds.length === 0) {
        if (selectedKitIds.length === 1) {
          // Only one main kit, no complement → offer a 2nd main kit.
          setUpsellStep('secondKit');
        } else {
          // Other 0-complement cases → kit + 1 complement offer.
          setUpsellStep('first');
        }
      } else if (selectedUpsellIds.length === 1) {
        // Mixed cart with exactly one complement → still offer a 2nd complement.
        setUpsellStep('extra');
      } else {
        // 2+ complements already → no extra offer, go straight to Shopify.
        setUpsellResolved(true);
        goToShopifyCart([...selectedKitIds, ...selectedUpsellIds]);
      }
      return;
    }
    goToShopifyCart([...selectedKitIds, ...selectedUpsellIds]);
  };
  // SECOND-KIT offer (2 products, both for $22.990): accept → add the chosen
  // second product to the right bucket (main kit or complement).
  const handleSecondKitAccept = (secondId: string, type: 'kit' | 'complement') => {
    const finalKitIds = type === 'kit' && !selectedKitIds.includes(secondId) ? [...selectedKitIds, secondId] : selectedKitIds;
    const finalUpsellIds = type === 'complement' && !selectedUpsellIds.includes(secondId) ? [...selectedUpsellIds, secondId] : selectedUpsellIds;
    if (type === 'kit') {
      setSelectedKitIds(finalKitIds);
    } else {
      setSelectedUpsellIds(finalUpsellIds);
    }
    setUpsellResolved(true);
    setUpsellStep('none');
    if (type === 'kit') {
      // Both products are main kits → use the kit+kit promo variant ids.
      goToShopifyCartTwoKitsPromo(finalKitIds);
    } else {
      // Kit + complement → use the standard 2-products promo variant ids.
      goToShopifyCartTwoProductsPromo([...finalKitIds, ...finalUpsellIds]);
    }
  };
  // FIRST offer (kit + 1 complement): accept → add complement and finish.
  const handleFirstAccept = (complementId: string) => {
    const finalUpsellIds = selectedUpsellIds.includes(complementId) ? selectedUpsellIds : [...selectedUpsellIds, complementId];
    setSelectedUpsellIds(finalUpsellIds);
    setUpsellResolved(true);
    setUpsellStep('none');
    goToShopifyCart([...selectedKitIds, ...finalUpsellIds]);
  };
  // EXTRA offer (2nd complement, both for $19.990): accept → add extra and
  // redirect using the PROMO variant ids for the complementary kits.
  const handleExtraAccept = (extraComplementId: string) => {
    const finalUpsellIds = selectedUpsellIds.includes(extraComplementId) ? selectedUpsellIds : [...selectedUpsellIds, extraComplementId];
    setSelectedUpsellIds(finalUpsellIds);
    setUpsellResolved(true);
    setUpsellStep('none');
    goToShopifyCartTwoComplementsPromo(selectedKitIds, finalUpsellIds);
  };
  // Declining either the first or the extra offer → escalate to the bundle.
  const handleEscalateToBundle = () => {
    setUpsellStep('bundle');
  };
  // BUNDLE offer (all 6 at 30% OFF): accept → add all and finish.
  const handleBundleAccept = () => {
    const allUpsellIds = UPSELLS.map(u => u.id);
    setSelectedUpsellIds(allUpsellIds);
    setUpsellResolved(true);
    setUpsellStep('none');
    // Full-bundle promo → complements use the bundle promo variant ids.
    goToShopifyCartBundlePromo(selectedKitIds, allUpsellIds);
  };
  // BUNDLE decline → resolve the funnel and go to Shopify as-is.
  const handleBundleDecline = () => {
    setUpsellResolved(true);
    setUpsellStep('none');
    goToShopifyCart([...selectedKitIds, ...selectedUpsellIds]);
  };
  const handleBuyAllKits = () => {
    const allKitIds = KITS.map(k => k.id);
    const allUpsellIds = UPSELLS.map(u => u.id);
    setSelectedKitIds(allKitIds);
    setSelectedUpsellIds(allUpsellIds);
    setUpsellResolved(true);
    // Pack Completo → redirect to the Shopify cart with the dedicated
    // 9-kit pack variant ids.
    goToShopifyCartFullPack();
  };
  const totalItems = selectedKitIds.length + selectedUpsellIds.length;
  const totalPrice = KITS.filter(k => selectedKitIds.includes(k.id)).reduce((sum, k) => sum + k.price, 0) + UPSELLS.filter(u => selectedUpsellIds.includes(u.id)).reduce((sum, u) => sum + u.price, 0);
  return <div className="min-h-screen bg-cream font-sans text-ink selection:bg-gold selection:text-white" data-id="element-0">
      <PremiumLoader data-id="element-1" />
      <ScrollProgressBar data-id="element-2" />
      <AnnouncementBar data-id="element-3" />
      <Navbar selectedKitIds={selectedKitIds} selectedUpsellIds={selectedUpsellIds} onRemoveUpsell={handleRemoveUpsell} onRemoveKit={handleRemoveKit} onCheckoutAttempt={handleCheckoutAttempt} data-id="element-4" />
      <main data-id="element-5">
        <Hero data-id="element-6" />
        <TrustBand data-id="element-7" />
        <BenefitsSection data-id="element-8" />
        <BeforeAfterSection data-id="element-9" />
        <ProductCollection selectedKitIds={selectedKitIds} selectedUpsellIds={selectedUpsellIds} onToggleKit={handleToggleKit} onToggleUpsell={handleToggleUpsell} data-id="element-10" />
        <CompleteCollection onBuyAll={handleBuyAllKits} data-id="element-11" />
        <PremiumGallery data-id="element-12" />
        <ContentPreview data-id="element-13" />
        <Testimonials data-id="element-14" />
        <div className="py-12 bg-white flex justify-center" data-id="element-15">
          <PaymentMethods data-id="element-16" />
        </div>
        <Guarantee data-id="element-17" />
        <div id="cart" className="scroll-mt-24" data-id="element-18" />
        <CartSummary selectedKitIds={selectedKitIds} selectedUpsellIds={selectedUpsellIds} onRemoveKit={handleRemoveKit} onRemoveUpsell={handleRemoveUpsell} onCheckoutAttempt={handleCheckoutAttempt} data-id="element-19" />
        <FAQ data-id="element-20" />
        <FinalCTA data-id="element-21" />
      </main>
      <Footer data-id="element-22" />
      <StickyMobileCTA onCheckoutAttempt={handleCheckoutAttempt} itemCount={totalItems} total={totalPrice} data-id="element-23" />
      <PurchaseToast data-id="element-24" />
      {/* Second-kit upsell: 2 main kits for $22.990 — when exactly 1 main kit, 0 complements */}
      <SecondKitUpsellPopup open={upsellStep === 'secondKit'} selectedKitIds={selectedKitIds} onAccept={handleSecondKitAccept} onDecline={handleEscalateToBundle} data-id="element-25" />
      {/* First upsell: kit + 1 complement ($22.980) — other 0-complement cases */}
      <ExitIntentUpsellPopup open={upsellStep === 'first'} selectedKitIds={selectedKitIds} onAccept={handleFirstAccept} onDecline={handleEscalateToBundle} data-id="element-26" />
      {/* Extra upsell: add a 2nd complement, both for $19.990 — when 1 complement */}
      <ExtraComplementUpsellPopup open={upsellStep === 'extra'} selectedKitIds={selectedKitIds} selectedUpsellIds={selectedUpsellIds} onAccept={handleExtraAccept} onDecline={handleEscalateToBundle} data-id="element-27" />
      {/* Final upsell: full bundle (escalation if an offer is declined) */}
      <BundleUpsellPopup open={upsellStep === 'bundle'} selectedKitIds={selectedKitIds} onAccept={handleBundleAccept} onDecline={handleBundleDecline} data-id="element-28" />
    </div>;
}